import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { SignalingConfigService } from './signaling-config.service';

/**
 * Service for requesting short-lived JWT tokens from backend.
 * Never signs JWT tokens in the browser.
 */
export interface SignalingTokenRequest {
  peerId: string;
  quoteId?: string;
}

export interface SignalingTokenResponse {
  token: string;
  expiresAt?: string;
}

interface JwtPayload {
  exp?: number;
  [key: string]: unknown;
}

const JWT_PART_COUNT = 3;
const UNIX_SECONDS_DIVISOR = 1000;

@Injectable({
  providedIn: 'root'
})
export class SignalingTokenService {
  constructor(
    private readonly signalingConfig: SignalingConfigService,
    private readonly http: HttpClient
  ) { }

  /**
   * Request a short-lived JWT token from backend.
   * Contract:
   * POST /api/v1/signaling/signal-token
   * Body: { peerId, quoteId? }
   * Response: { token, expiresAt? }
   */
  async requestToken(peerId: string, quoteId?: string): Promise<SignalingTokenResponse | null> {
    const tokenEndpoint = this.signalingConfig.getTokenEndpoint();
    if (!tokenEndpoint) {
      console.warn('[SignalingToken] Token endpoint not configured');

      return null;
    }

    const body: SignalingTokenRequest = quoteId ? { peerId, quoteId } : { peerId };

    try {
      const response = await firstValueFrom(
        this.http.post<SignalingTokenResponse>(tokenEndpoint, body)
      );

      if (!response?.token) {
        console.warn('[SignalingToken] Token response missing token field');

        return null;
      }

      return response;
    } catch (error) {
      console.warn('[SignalingToken] Failed to request token from backend', error);

      return null;
    }
  }

  /**
   * Convenience method that returns token string directly.
   */
  async getToken(peerId: string, quoteId?: string): Promise<string | null> {
    const response = await this.requestToken(peerId, quoteId);

    return response?.token ?? null;
  }

  /**
   * Parse and validate a JWT token (client-side validation only).
   * Note: Full validation should happen server-side.
   */
  parseToken(token: string): JwtPayload | null {
    try {
      const parts = token.split('.');
      if (parts.length !== JWT_PART_COUNT) {
        throw new Error('Invalid JWT format');
      }

      const payloadBase64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const decodedPayload = window.atob(payloadBase64);
      const payload = JSON.parse(decodedPayload) as JwtPayload;
      const now = Math.floor(Date.now() / UNIX_SECONDS_DIVISOR);

      if (payload.exp && payload.exp < now) {
        console.warn('[SignalingToken] Token is expired');

        return null;
      }

      return payload;
    } catch (error) {
      console.error('[SignalingToken] Failed to parse token:', error);

      return null;
    }
  }

  /**
   * Check if a token is valid (not expired).
   */
  isTokenValid(token: string): boolean {
    const payload = this.parseToken(token);

    return payload !== null;
  }
}
